import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {PlayerSessionShift, SessionVideoSegment} from '../../interfaces';
import VideoPlayer from './VideoPlayer/VideoPlayer';
import PlayerSessionShiftSlider from '../PlayerSessions/PlayerSessionShiftSlider';
import {Alert, Box, Button, CircularProgress} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from '../../store/hooks';
import {Roles} from '../../enums';

export interface SessionVideoPlayerItem {
  id: number | string;
  videoUrl?: string;
}

interface Props {
  shiftClips?: PlayerSessionShift[];
  videoItems?: SessionVideoPlayerItem[];
  sessionVideoSegments?: SessionVideoSegment[];
  sessionType?: string;
  selectedVideoUrl?: string | null;
  renderCard?: (args: {
    item: SessionVideoPlayerItem;
    index: number;
    active: boolean;
    onClick: () => void;
  }) => React.ReactNode;
}

const SessionVideoPlayer: React.FC<Props> = ({
  shiftClips = [],
  videoItems,
  sessionVideoSegments,
  sessionType,
  selectedVideoUrl,
  renderCard,
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [videoIndex, setVideoIndex] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const {teamId, teamSessionId, profileId, playerSessionId} = useParams<{
    teamId: string;
    teamSessionId: string;
    profileId: string;
    playerSessionId: string;
  }>();
  const playerSession = useAppSelector(
    state => state.playerSession?.playerSession,
  );
  const playerSessionIdInt = playerSessionId ? parseInt(playerSessionId) : null;
  const role = useAppSelector(state => state.profile?.profile?.role);

  const requiresCoachOrAdmin = useMemo(
    () =>
      !!playerSession?.teamSession &&
      ![Roles.coach, Roles.admin].includes(role),
    [playerSession?.teamSession, role],
  );

  // Build the align-shift-video link from the current route context so back
  // navigation works from team and family pages (mirrors AlignShiftVideo's
  // backUrl logic).
  const alignShiftVideoUrl = useMemo(() => {
    if (teamId && teamSessionId && playerSessionId) {
      return `/teams/${teamId}/teamsessions/${teamSessionId}/playersessions/${playerSessionId}/align-shift-video`;
    } else if (teamId && profileId && playerSessionId) {
      return `/teams/${teamId}/players/${profileId}/playersessions/${playerSessionId}/align-shift-video`;
    } else if (profileId && playerSessionId) {
      return `/family/children/${profileId}/playersessions/${playerSessionId}/align-shift-video`;
    }
    return `/playersessions/${playerSessionId}/align-shift-video`;
  }, [teamId, teamSessionId, profileId, playerSessionId]);

  const sourceList = videoItems ?? shiftClips;
  const sourceUrl = videoItems
    ? videoItems?.[videoIndex]?.videoUrl
    : shiftClips?.[videoIndex]?.playerSessionVideoClips?.[0]?.url;

  useEffect(() => {
    if (videoIndex > sourceList.length - 1) {
      setVideoIndex(0);
    }
  }, [sourceList.length, videoIndex]);

  useEffect(() => {
    if (!selectedVideoUrl || !videoItems) return;
    const idx = videoItems.findIndex(v => v.videoUrl === selectedVideoUrl);
    if (idx !== -1 && idx !== videoIndex) {
      setVideoIndex(idx);
    }
  }, [selectedVideoUrl, videoItems]);

  // Reload video when sourceUrl changes
  useEffect(() => {
    videoRef.current?.load();
  }, [sourceUrl]);

  if (sourceList.length === 0) {
    return null;
  }

  return (
    <>
      <VideoPlayer
        videoRef={videoRef}
        sourceUrl={sourceUrl}
        sessionType={sessionType}
        shiftClips={shiftClips}
        videoIndex={videoIndex}
        setVideoIndex={setVideoIndex}
      />
      <PlayerSessionShiftSlider
        sessionType={sessionType}
        shiftClips={shiftClips}
        items={videoItems}
        videoIndex={videoIndex}
        setVideoIndex={setVideoIndex}
        videoRef={videoRef}
        renderCard={renderCard}
      />
      {playerSessionIdInt && (sessionVideoSegments ?? []).length > 0 && (
        <Box sx={{display: 'flex', mt: 2}}>
          <Button
            sx={{mr: 2}}
            variant="contained"
            color="primary"
            disabled={
              !!playerSession?.videoProcessingStartedAt || requiresCoachOrAdmin
            }
            onClick={() => navigate(alignShiftVideoUrl)}>
            {t('shiftAdjustment.alignShiftVideo')}
          </Button>
          {!!playerSession?.videoProcessingStartedAt && (
            <>
              <Alert severity="info" sx={{mr: 2}}>
                {t('shiftAdjustment.reprocessingVideo')}
                <CircularProgress sx={{color: '#fff', ml: 1}} size={16} />
              </Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  window.location.reload();
                }}>
                {t('general.refresh')}
              </Button>
            </>
          )}
          {requiresCoachOrAdmin && (
            <Alert severity="info" sx={{mr: 2}}>
              {t('shiftAdjustment.unableToEditShiftVideoForTeamSession')}
            </Alert>
          )}
        </Box>
      )}
    </>
  );
};

export default SessionVideoPlayer;
